import {
  PCM_FRAME_DURATION_MS,
  PCM_FRAMES_PER_CHUNK,
} from "./audio-config";
import type { AudioFrame } from "./pcm-framer";

export type { AudioFrame } from "./pcm-framer";

/** An ordered group of normal ten-frame (approximately 200 ms) PCM frames. */
export interface RawChunk {
  sequenceNumber: number;
  frames: AudioFrame[];
  durationMs: number;
}

export interface PcmChunkerOptions {
  framesPerChunk?: number;
  startingSequenceNumber?: number;
}

/**
 * Produces ordered normal chunks, retaining any short tail for `flush` rather
 * than silently discarding it at pause or stop time.
 */
export class PcmChunker {
  private readonly framesPerChunk: number;
  private frames: AudioFrame[] = [];
  private nextSequence: number;

  constructor({
    framesPerChunk = PCM_FRAMES_PER_CHUNK,
    startingSequenceNumber = 0,
  }: PcmChunkerOptions = {}) {
    if (!Number.isInteger(framesPerChunk) || framesPerChunk <= 0) {
      throw new RangeError("framesPerChunk must be a positive integer.");
    }
    if (!Number.isInteger(startingSequenceNumber) || startingSequenceNumber < 0) {
      throw new RangeError("startingSequenceNumber must be a non-negative integer.");
    }

    this.framesPerChunk = framesPerChunk;
    this.nextSequence = startingSequenceNumber;
  }

  get bufferedFrameCount(): number {
    return this.frames.length;
  }

  get nextSequenceNumber(): number {
    return this.nextSequence;
  }

  addFrame(frame: AudioFrame): RawChunk[] {
    this.frames.push(frame);
    const chunks: RawChunk[] = [];

    while (this.frames.length >= this.framesPerChunk) {
      chunks.push(this.takeChunk(this.frames.splice(0, this.framesPerChunk)));
    }

    return chunks;
  }

  /** Alias suitable for stream-style call sites. */
  push(frame: AudioFrame): RawChunk[] {
    return this.addFrame(frame);
  }

  addFrames(frames: Iterable<AudioFrame>): RawChunk[] {
    const chunks: RawChunk[] = [];
    for (const frame of frames) chunks.push(...this.addFrame(frame));
    return chunks;
  }

  flush(): RawChunk | undefined {
    if (this.frames.length === 0) return undefined;
    return this.takeChunk(this.frames.splice(0));
  }

  reset(startingSequenceNumber = 0): void {
    if (!Number.isInteger(startingSequenceNumber) || startingSequenceNumber < 0) {
      throw new RangeError("startingSequenceNumber must be a non-negative integer.");
    }
    this.frames = [];
    this.nextSequence = startingSequenceNumber;
  }

  private takeChunk(frames: AudioFrame[]): RawChunk {
    const durationMs = frames.reduce(
      (total, frame) => total + (frame.durationMs ?? PCM_FRAME_DURATION_MS),
      0,
    );
    const chunk: RawChunk = {
      sequenceNumber: this.nextSequence,
      frames,
      durationMs,
    };
    this.nextSequence += 1;
    return chunk;
  }
}
